import { z } from "zod";
import { appearanceOverridesSchema, chatTextSchema } from "./appearance";
import {
	chatStartersSchema,
	chatStateSchema,
	RESTRICTABLE_KINDS,
} from "./control";
import { localeSchema } from "./url";

/**
 * Bridge envelope v1 (docs/integrations/ARCHITECTURE.md ADR-5.4): the messages the hosted page
 * and its host app (Flutter, React Native, iframe parent, widget loader) exchange. Callers
 * validate with `.safeParse`. The shopper session id and token never cross the bridge.
 */

function message<T extends string, D extends z.ZodType>(type: T, data: D) {
	return z.object({
		v: z.literal(1),
		/** Idempotency key: the DomainEvent id when one exists. Events are at-least-once. */
		id: z.string().min(1).max(200),
		type: z.literal(type),
		/** Epoch milliseconds. */
		ts: z.number().int().nonnegative(),
		data,
	});
}

const substitution = z.enum(["SIMILAR", "SPECIFIC", "NONE"]);
const empty = z.object({});

/**
 * A product in the retailer's own ids. `itemId` is its id in the store of the session: what a
 * cart or an order line there is keyed on. `catalogId` is its id in the catalog, the same in
 * every store. A platform with one id per product sends the same value in both.
 */
const ids = { itemId: z.string(), catalogId: z.string() };

export const cartItemEventSchema = z.object({
	storeId: z.string(),
	conversationId: z.string(),
	origin: z.enum(["agent", "shopper", "host"]),
	item: z.object({
		...ids,
		quantity: z.number().int().nonnegative(),
		previousQuantity: z.number().int().nonnegative(),
		/** Decimal string in the store currency. */
		unitPrice: z.string(),
		currency: z.string(),
		title: z.string(),
		/** The product's main image, an absolute URL. Absent when it has none. */
		imageUrl: z.string().optional(),
		substitution: substitution.optional(),
		/** With SPECIFIC: the item to pack instead, by its id in the same store. */
		substituteItemId: z.string().optional(),
	}),
});
export type CartItemEvent = z.infer<typeof cartItemEventSchema>;

/** The basket handed to the host at checkout, in the host's own ids. */
export const cartHandoffSchema = z.object({
	storeId: z.string(),
	currency: z.string(),
	total: z.string(),
	conversationId: z.string(),
	externalShopperId: z.string().optional(),
	hostSessionId: z.string().optional(),
	items: z.array(
		z.object({
			...ids,
			quantity: z.number().int().positive(),
			unitPrice: z.string(),
			substitution: z.string().optional(),
			substituteItemId: z.string().optional(),
		}),
	),
});
export type CartHandoff = z.infer<typeof cartHandoffSchema>;

/** Whole milliseconds, at most an hour: a page's own marks, sent unauthenticated. */
const ms = z.number().int().nonnegative().max(3_600_000);

/**
 * How one open of the chat went (SDK-PRELOAD.md 2.1). Every value is rounded by the page.
 * SDKs hand `open` and `preloaded` to the app; the rest are the page's own marks, kept on the
 * shopper session for the Install screen.
 */
export const openTimingSchema = z.object({
	/** Epoch ms: `Math.round(performance.timeOrigin)`. */
	navigationStart: z.number().int().nonnegative(),
	/** The rest are whole ms after navigationStart. */
	responseStart: ms.optional(),
	/** The skeleton mark the inline script leaves, not a paint entry. */
	firstPaint: ms,
	/** `ready` posted. */
	bridgeReady: ms,
	/** Hydrated, session settled, composer enabled. */
	interactive: ms,
	/**
	 * ms from the host's `visibility.at` to the chat usable and a frame presented. Absent before
	 * SDK 1.1.0, and absent when no frame could be confirmed.
	 */
	open: ms.optional(),
	/** The chat was already usable when this show began. */
	preloaded: z.boolean(),
});
export type OpenTiming = z.infer<typeof openTimingSchema>;

export const pageToHostSchema = z.discriminatedUnion("type", [
	message(
		"ready",
		z.object({
			orgSlug: z.string(),
			storeId: z.string(),
			conversationId: z.string().optional(),
			hostSessionId: z.string().optional(),
			capabilities: z.array(z.string()),
		}),
	),
	message("cart.item_added", cartItemEventSchema),
	message("cart.item_updated", cartItemEventSchema),
	message("cart.item_removed", cartItemEventSchema),
	message(
		"cart.cleared",
		z.object({
			storeId: z.string(),
			removed: z.array(cartItemEventSchema.shape.item),
			conversationId: z.string(),
		}),
	),
	message("checkout", cartHandoffSchema),
	message(
		"handoff",
		z.object({ conversationId: z.string(), reason: z.string().optional() }),
	),
	message("open_product", z.object({ ...ids, storeId: z.string() })),
	message("session.expired", empty),
	/** Widget only: the iframe's content height in CSS pixels. */
	message("resize", z.object({ height: z.number().nonnegative() })),
	message("close", empty),
	/**
	 * The server-rendered chat is in the document: hide the native spinner. Posted by an inline
	 * script that waits for no module, to native webviews only. SDKs before 1.1.0 drop it.
	 */
	message("painted", empty),
	/** The chat became usable, or was shown again. Native webviews only; dropped before 1.1.0. */
	message("timing", openTimingSchema),
	/** What the chat is doing, on every change (since SDK 2.2.0). */
	message("state", chatStateSchema),
	/**
	 * Debug mode only (`debug=1`, an SDK's `debug: true`): one line of the page's trace, for the
	 * SDK to print in the app's log. Never a token or what the shopper wrote. Since SDK 2.3.0;
	 * older SDKs drop it.
	 */
	message(
		"log",
		z.object({
			level: z.enum(["info", "warn", "error"]),
			message: z.string().max(2000),
		}),
	),
]);
export type PageToHost = z.infer<typeof pageToHostSchema>;

/** Delivered by the host as `window.hephon.receive(msg)` (webviews) or postMessage (iframes). */
export const hostToPageSchema = z.discriminatedUnion("type", [
	message(
		"cart.snapshot",
		z.object({
			/**
			 * Each line by the id the app's cart holds: `itemId` (the product in this store),
			 * or `catalogId` for a cart keyed on catalog ids. One of the two, never both.
			 */
			items: z.array(
				z
					.object({
						itemId: z.string().min(1).optional(),
						catalogId: z.string().min(1).optional(),
						quantity: z.number().int().nonnegative(),
					})
					.refine(
						(item) =>
							(item.itemId === undefined) !==
							(item.catalogId === undefined),
					),
			),
		}),
	),
	message("session.launch", z.object({ launchToken: z.string().min(1) })),
	/** The chat follows the app's light or dark mode. Short for `appearance` with a scheme. */
	message("theme", z.object({ scheme: z.enum(["light", "dark"]) })),
	/**
	 * Restyle the chat while it is open; merged over what the app sent before (and over the
	 * URL's overrides). Ignored while the retailer has app overrides off: `ready` lists the
	 * `appearance` capability when they are on.
	 */
	message("appearance", appearanceOverridesSchema),
	/** Replace wording (titles, button labels); merged like `appearance`, under the same switch. */
	message("text", chatTextSchema),
	message("focus", empty),
	/**
	 * Whether the chat is on screen (a preloaded or kept-warm web view is not). `at`: epoch ms
	 * when the host learned of the open; only on the first delivery of a show, absent on a
	 * re-send and on a hide. Since SDK 1.1.0.
	 */
	message(
		"visibility",
		z.object({
			shown: z.boolean(),
			at: z.number().int().nonnegative().optional(),
		}),
	),
	// Since SDK 2.2.0 (control.ts), each listed in ready.capabilities.
	/** Send this as the shopper's message, as if typed. Dropped while a reply is on its way. */
	message(
		"chat.send",
		z.object({ text: z.string().trim().min(1).max(2000) }),
	),
	/** Start a new conversation, with an empty chat basket; the app's own cart is not touched. */
	message("chat.new", empty),
	message("basket.open", empty),
	message("basket.close", empty),
	/** The welcome screen's suggestions, under the same switch as `appearance`. */
	message("starters", chatStartersSchema),
	/** The app's language: the chat's own wording and the reply language until the shopper writes. */
	message("locale", z.object({ locale: localeSchema })),
	/** Kinds this chat never shows, added to the retailer's (it can never lift one of them). */
	message(
		"restrictions",
		z.object({ exclude: z.array(z.enum(RESTRICTABLE_KINDS)).max(8) }),
	),
]);
export type HostToPage = z.infer<typeof hostToPageSchema>;

export type BridgeMessage = PageToHost | HostToPage;
